import { useEffect, type FormEvent } from "react";
import { useForm } from "../useForm";

import InputTextOnly from "../../controls/InputTextOnly";
import { convertToDefEventPara } from "../../../shared/utility";
import SelectMenu from "../../controls/SelectMenu";
import TextArea from "../../controls/TextArea";
import type { SelectOption } from "../../../types/api";
import type { CustomerRecord, TaxRateOption, UpdateCustomerPayload } from "../../../types/customers";

const initialFValues: CustomerRecord = {
  id: 0,
  company_name: "",
  email: "",
  given_name: "",
  middle_name: "",
  family_name: "",
  bill_with_parent: "",
  primary_phone_number: "",
  active: 0,
  customer_groups: [],
  allocated_to: [],
  sales_reps: [],
  tax_rates: [],
  customer_group: null,
  image: "",
};

interface CustomerDetailsFormProps {
  customer: CustomerRecord;
  customerGroupOptions: SelectOption[];
  actionCall: (values: UpdateCustomerPayload) => void | Promise<void>;
  usersOptions: SelectOption[];
  taxRateOptions: TaxRateOption[];
}

export default function CustomerDetailsForm(props: CustomerDetailsFormProps) {
  const { customer, customerGroupOptions, actionCall, usersOptions, taxRateOptions } = props;

  const { values, setValues, errors, setErrors, handleInputChange, resetForm } = useForm(
    initialFValues,
    false
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    actionCall(values);
  };
  const userType = localStorage.getItem("userType");

  useEffect(() => {
    if (customer) {
      setValues(customer);
    }
  }, [customer, setValues]);

  const customerGroupValue = (() => {
    const sel = values.customer_group;
    if (sel == null) return null;
    const opts = customerGroupOptions;
    if (!Array.isArray(opts)) return sel;
    const found = opts.find((o) => String(o.value) === String(sel.value));
    return found ?? sel;
  })();

  return (
    <form onSubmit={handleSubmit}>
      <div className="p-4">
        <div className="space-y-3 grid grid-cols-3 gap-5">
          <div className="col-span-2">
            <div className="mt-3 grid grid-cols-1 gap-4">
              <div>
                <InputTextOnly
                  label="Company Name"
                  name="company_name"
                  onChange={handleInputChange}
                  value={values.company_name ?? ""}
                  labelOn={true}
                  disabled={true}
                />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-4">
              <SelectMenu
                handleInputChange={handleInputChange}
                name="tax_rates"
                value={values.tax_rates ?? []}
                options={taxRateOptions ?? []}
                title="Tax rates"
                isMulti={true}
                isClearable={true}
                placeholder="No tax rates assigned"
              />
              <p className="text-xs text-gray-500">
                Combined default:{" "}
                {(values.tax_rates ?? []).reduce((sum, rate) => sum + Number(rate.rate ?? 0), 0)}%.
                Individual order items can override this rate.
              </p>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-4">
              <div className="col-span-2 flex">
                <input
                  id="active"
                  aria-describedby="selectAllBox"
                  name="active"
                  type="checkbox"
                  checked={Boolean(values.active)}
                  className="mt-1 mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  onChange={(e) =>
                    handleInputChange(convertToDefEventPara("active", e.target.checked))
                  }
                />
                Active
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-4">
              <SelectMenu
                handleInputChange={handleInputChange}
                name="customer_group"
                value={customerGroupValue}
                options={customerGroupOptions ?? []}
                title={"Customer group"}
                isMulti={false}
                isClearable={true}
                placeholder="Select customer group..."
              />
            </div>
            <div className="mt-3 grid grid-cols-1 gap-4">
              {Number(userType) === 1 ? (
                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="sales_reps"
                  value={values.sales_reps ?? []}
                  options={usersOptions ?? []}
                  title={"Sales persons"}
                  isMulti={true}
                />
              ) : null}
            </div>
            <div className="mt-3 grid grid-cols-1 gap-4">
              <div className="col-span-2 flex">
                <input
                  id="taxable"
                  aria-describedby="selectAllBox"
                  name="taxable"
                  type="checkbox"
                  checked={Boolean(values.taxable)}
                  className="mt-1 mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  onChange={(e) =>
                    handleInputChange(convertToDefEventPara("taxable", e.target.checked))
                  }
                />
                Taxable
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-4">
              <div>
                <InputTextOnly
                  label="Quickbooks Id"
                  name="quickbooks_id"
                  onChange={handleInputChange}
                  value={values.quickbooks_id == null ? "" : String(values.quickbooks_id)}
                  labelOn={true}
                  disabled={true}
                />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-4">
              <div>
                <InputTextOnly
                  label="Billing Email Address"
                  name="email"
                  onChange={handleInputChange}
                  value={values.email ?? ""}
                  labelOn={true}
                  disabled={true}
                />
              </div>
              <div>
                <InputTextOnly
                  label="Phone Number"
                  name="primary_phone_number"
                  onChange={handleInputChange}
                  value={values.primary_phone_number ?? ""}
                  labelOn={true}
                  disabled={true}
                />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-4">
              <div>
                <InputTextOnly
                  label="Contact Given Name"
                  name="given_name"
                  onChange={handleInputChange}
                  value={values.given_name ?? ""}
                  labelOn={true}
                  disabled={true}
                />
              </div>
              <div>
                <InputTextOnly
                  label="Contact Last Name"
                  name="family_name"
                  onChange={handleInputChange}
                  value={values.family_name ?? ""}
                  labelOn={true}
                  disabled={true}
                />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between flex-wrap sm:flex-nowrap">
              <div>
                <h3 className="text-md leading-6 font-medium text-gray-900">Notes</h3>
              </div>
            </div>
            <TextArea
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm max-w-lg"
              labelOn={false}
              name="notes"
              onChange={(e) => handleInputChange(convertToDefEventPara("notes", e.target.value))}
              value={values.notes ?? ""}
              rows="5"
            />
          </div>

          <div className="sm:flex-no-wrap col-span-3">
            <div className="my-auto flex items-center">
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-main-purple hover:bg-indigo-700 items-center mt-3"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
